<template>
  <div>
    <TopNav />

    <main class="dashboard">
      <div class="dashboard-header dashboard-header-row">
        <div>
          <h1 class="dashboard-title">Servicepartner</h1>
          <p class="dashboard-subtitle">Stammdaten der externen Servicepartner.</p>
        </div>
        <button class="btn btn-primary" @click="openCreateModal" :disabled="loading">
          Neuer Servicepartner
        </button>
      </div>

      <div v-if="loading" class="spinner" aria-label="Lädt"></div>
      <div v-else-if="loadError" class="error">{{ loadError }}</div>

      <div v-else-if="items.length === 0" class="empty-state">
        Keine Servicepartner vorhanden.
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle servicepartner-table">
          <thead>
            <tr>
              <th scope="col" style="width: 70px;">ID</th>
              <th scope="col">Bezeichnung (intern)</th>
              <th scope="col">Öffentl. Bezeichnung</th>
              <th scope="col">Ansprechpartner</th>
              <th scope="col" style="width: 90px;">Sprache</th>
              <th scope="col" style="width: 60px;">Land</th>
              <th scope="col">Mail</th>
              <th scope="col" class="sp-actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sp in items" :key="sp.id">
              <td data-label="ID">{{ sp.id }}</td>
              <td data-label="Bezeichnung (intern)">{{ sp.description }}</td>
              <td data-label="Öffentl. Bezeichnung">{{ sp.public_name ?? '—' }}</td>
              <td data-label="Ansprechpartner">{{ sp.service_contact ?? '—' }}</td>
              <td data-label="Sprache">{{ sp.preferred_language?.description ?? '—' }}</td>
              <td data-label="Land">{{ sp.country_code }}</td>
              <td data-label="Mail">{{ sp.mail }}</td>
              <td class="text-end sp-actions-cell" data-label="Aktionen">
                <button
                  class="btn btn-sm btn-outline-secondary me-1"
                  :disabled="busyId === sp.id"
                  @click="openAccounts(sp)"
                >Zugänge</button>
                <button
                  class="btn btn-sm btn-outline-secondary me-1"
                  :disabled="busyId === sp.id"
                  @click="openEditModal(sp)"
                >Bearbeiten</button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  :disabled="busyId === sp.id"
                  @click="onDelete(sp)"
                >Löschen</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <ServicepartnerAccountsModal ref="accountsModal" />

    <!-- Bootstrap-Modal: Servicepartner anlegen/bearbeiten -->
    <div
      ref="modalEl"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="spModalTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <!-- Nach dem Anlegen endet der Weg nicht in einer Liste, in der man den
               neuen Partner erst suchen muss: der Zugang kann gleich hier folgen. -->
          <template v-if="justCreated">
            <div class="modal-header">
              <h5 class="modal-title">Servicepartner angelegt</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-success mb-3" role="alert">
                „{{ justCreated.description }}" ist angelegt.
              </div>
              <p class="mb-0">
                Soll für diesen Partner gleich ein Zugang zum HandyShop Partnerportal
                angelegt werden? Das ist auch später über „Zugänge" möglich.
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="onCreateLater">Später</button>
              <button type="button" class="btn btn-primary" @click="onCreateAccountForNew">Zugang anlegen</button>
            </div>
          </template>

          <form v-else @submit.prevent="onSubmit" novalidate>
            <div class="modal-header">
              <h5 class="modal-title" id="spModalTitle">
                {{ mode === 'edit' ? 'Servicepartner bearbeiten' : 'Neuer Servicepartner' }}
              </h5>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Schließen"
              ></button>
            </div>

            <div class="modal-body">
              <div v-if="submitError" class="alert alert-danger" role="alert">
                {{ submitError }}
              </div>

              <div class="mb-3">
                <label for="sp-description" class="form-label">Bezeichnung (intern)</label>
                <input
                  id="sp-description"
                  v-model="form.description"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('description') }"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="sp-public-name" class="form-label">Öffentliche Bezeichnung</label>
                <input
                  id="sp-public-name"
                  v-model="form.public_name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('public_name') }"
                  maxlength="128"
                />
                <div class="form-text">Für Kunden sichtbarer Name. Leer lassen, wenn die interne Bezeichnung genügt.</div>
              </div>
              <div class="mb-3">
                <label for="sp-service-contact" class="form-label">Ansprechpartner</label>
                <input
                  id="sp-service-contact"
                  v-model="form.service_contact"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('service_contact') }"
                  maxlength="64"
                />
              </div>
              <div class="mb-3">
                <label for="sp-language" class="form-label">Kommunikationssprache</label>
                <select
                  id="sp-language"
                  v-model.number="form.preferred_language_id"
                  class="form-select"
                  :class="{ 'is-invalid': invalidFields.has('preferred_language_id') }"
                  :disabled="languages.length === 0"
                >
                  <option
                    v-for="lang in languages"
                    :key="lang.id"
                    :value="lang.id"
                  >{{ lang.description }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="sp-adr" class="form-label">Adresse</label>
                <input
                  id="sp-adr"
                  v-model="form.adr"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('adr') }"
                  required
                />
              </div>
              <div class="row">
                <div class="col-4 mb-3">
                  <label for="sp-plz" class="form-label">PLZ</label>
                  <input
                    id="sp-plz"
                    v-model="form.plz"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': invalidFields.has('plz') }"
                    required
                  />
                </div>
                <div class="col-8 mb-3">
                  <label for="sp-town" class="form-label">Ort</label>
                  <input
                    id="sp-town"
                    v-model="form.town"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': invalidFields.has('town') }"
                    required
                  />
                </div>
              </div>
              <div class="mb-3">
                <label for="sp-country" class="form-label">Land</label>
                <select
                  id="sp-country"
                  v-model="form.country_code"
                  class="form-select"
                  :class="{ 'is-invalid': invalidFields.has('country_code') }"
                  :disabled="countries.length === 0"
                  required
                >
                  <option
                    v-for="c in countries"
                    :key="c.code"
                    :value="c.code"
                  >{{ c.description }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="sp-mail" class="form-label">Mail</label>
                <input
                  id="sp-mail"
                  v-model="form.mail"
                  type="email"
                  class="form-control"
                  :class="{ 'is-invalid': invalidFields.has('mail') }"
                  required
                />
              </div>
              <div class="mb-3">
                <label for="sp-wants-invoices" class="form-label">Kundenrechnung</label>
                <select
                  id="sp-wants-invoices"
                  v-model="form.wants_customer_invoices_raw"
                  class="form-select"
                  :class="{ 'is-invalid': invalidFields.has('wants_customer_invoices') }"
                  required
                >
                  <option value="">— Bitte wählen —</option>
                  <option value="yes">Ja</option>
                  <option value="no">Nein</option>
                </select>
              </div>
              <div class="form-check">
                <input
                  id="sp-instant-slip-closing"
                  v-model="form.instant_slip_closing"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="sp-instant-slip-closing" class="form-check-label">
                  Sofortiger LS-Abschluss (interner Servicepartner)
                </label>
                <div class="form-text">
                  Lieferschein und Rücklieferschein werden sofort abgeschlossen — ohne Abscannen.
                </div>
              </div>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
                :disabled="submitting"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="submitting"
              >
                {{ submitting ? 'Speichert …' : 'Speichern' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import TopNav from '../components/TopNav.vue';
import ServicepartnerAccountsModal from '../components/ServicepartnerAccountsModal.vue';
import {
  list,
  listCountries,
  listLanguages,
  create,
  update,
  disable,
  ApiError,
  type Country,
  type LanguageRef,
  type Servicepartner,
  type ServicepartnerPayload,
  type ServicepartnerUpdatePayload,
} from '../services/servicepartner';

const DEFAULT_COUNTRY_CODE  = 'AT';
const DEFAULT_LANGUAGE_ID   = 1;

interface FormState {
  description:                  string;
  public_name:                  string;
  service_contact:              string;
  preferred_language_id:        number;
  adr:                          string;
  plz:                          string;
  town:                         string;
  country_code:                 string;
  mail:                         string;
  wants_customer_invoices_raw:  '' | 'yes' | 'no';
  instant_slip_closing:         boolean;
}

const items     = ref<Servicepartner[]>([]);
const countries = ref<Country[]>([]);
const languages = ref<LanguageRef[]>([]);
const loading   = ref(true);
const loadError = ref<string | null>(null);
const busyId    = ref<number | null>(null);

const mode          = ref<'create' | 'edit'>('create');
const editId        = ref<number | null>(null);
const form          = ref<FormState>(emptyForm());
const submitting    = ref(false);
const submitError   = ref<string | null>(null);
const invalidFields = ref<Set<string>>(new Set());

const modalEl  = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

// Nach dem Anlegen gefuellt: dann zeigt der Dialog das Angebot, gleich einen
// Zugang anzulegen, statt einfach zuzugehen.
const justCreated    = ref<Servicepartner | null>(null);
const accountsModal  = useTemplateRef<{ open: (id: number, name: string, startInCreate?: boolean) => Promise<void> }>('accountsModal');

onMounted(async () => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
  }
  await Promise.all([reload(), loadCountriesOnce(), loadLanguagesOnce()]);
});

async function reload(): Promise<void> {
  loading.value   = true;
  loadError.value = null;
  try {
    items.value = await list();
  } catch (e) {
    loadError.value = errorMessage(e);
  } finally {
    loading.value = false;
  }
}

async function loadCountriesOnce(): Promise<void> {
  try {
    countries.value = await listCountries();
  } catch (e) {
    console.error('Country-Liste fehlgeschlagen:', e);
  }
}

async function loadLanguagesOnce(): Promise<void> {
  try {
    languages.value = await listLanguages();
  } catch (e) {
    console.error('Language-Liste fehlgeschlagen:', e);
  }
}

function emptyForm(): FormState {
  return {
    description:                  '',
    public_name:                  '',
    service_contact:              '',
    preferred_language_id:        DEFAULT_LANGUAGE_ID,
    adr:                          '',
    plz:                          '',
    town:                         '',
    country_code:                 DEFAULT_COUNTRY_CODE,
    mail:                         '',
    wants_customer_invoices_raw:  '',
    instant_slip_closing:         false,
  };
}

function openCreateModal(): void {
  justCreated.value = null;
  mode.value          = 'create';
  editId.value        = null;
  form.value          = emptyForm();
  submitError.value   = null;
  invalidFields.value = new Set();
  modal?.show();
}

function openEditModal(sp: Servicepartner): void {
  justCreated.value = null;
  mode.value          = 'edit';
  editId.value        = sp.id;
  form.value          = {
    description:                  sp.description ?? '',
    public_name:                  sp.public_name ?? '',
    service_contact:              sp.service_contact ?? '',
    preferred_language_id:        sp.preferred_language?.id ?? DEFAULT_LANGUAGE_ID,
    adr:                          sp.adr ?? '',
    plz:                          sp.plz ?? '',
    town:                         sp.town ?? '',
    country_code:                 sp.country_code || DEFAULT_COUNTRY_CODE,
    mail:                         sp.mail ?? '',
    wants_customer_invoices_raw:  sp.wants_customer_invoices === null
                                    ? ''
                                    : (sp.wants_customer_invoices ? 'yes' : 'no'),
    instant_slip_closing:         sp.instant_slip_closing ?? false,
  };
  submitError.value   = null;
  invalidFields.value = new Set();
  modal?.show();
}

async function onSubmit(): Promise<void> {
  submitError.value   = null;
  invalidFields.value = new Set();

  const missing: string[] = (['description', 'adr', 'plz', 'town', 'mail', 'country_code'] as const)
    .filter(k => form.value[k].trim() === '');

  if (form.value.wants_customer_invoices_raw === '') {
    missing.push('wants_customer_invoices');
  }

  if (missing.length > 0) {
    invalidFields.value = new Set(missing);
    submitError.value   = 'Bitte alle Pflichtfelder ausfüllen.';
    return;
  }

  const wantsCustomerInvoices = form.value.wants_customer_invoices_raw === 'yes';
  const serviceContact        = form.value.service_contact.trim();
  const publicName            = form.value.public_name.trim();

  submitting.value = true;
  try {
    if (mode.value === 'edit' && editId.value !== null) {
      const payload: ServicepartnerUpdatePayload = {
        description:              form.value.description.trim(),
        public_name:              publicName === '' ? null : publicName,
        adr:                      form.value.adr.trim(),
        plz:                      form.value.plz.trim(),
        town:                     form.value.town.trim(),
        mail:                     form.value.mail.trim(),
        country_code:             form.value.country_code.trim(),
        service_contact:          serviceContact === '' ? null : serviceContact,
        preferred_language_id:    form.value.preferred_language_id,
        wants_customer_invoices:  wantsCustomerInvoices,
        instant_slip_closing:     form.value.instant_slip_closing,
      };
      await update(editId.value, payload);
    } else {
      const payload: ServicepartnerPayload = {
        description:              form.value.description.trim(),
        public_name:              publicName === '' ? null : publicName,
        adr:                      form.value.adr.trim(),
        plz:                      form.value.plz.trim(),
        town:                     form.value.town.trim(),
        mail:                     form.value.mail.trim(),
        country_code:             form.value.country_code.trim(),
        service_contact:          serviceContact === '' ? null : serviceContact,
        preferred_language_id:    form.value.preferred_language_id,
        wants_customer_invoices:  wantsCustomerInvoices,
        instant_slip_closing:     form.value.instant_slip_closing,
      };
      justCreated.value = await create(payload);
    }
    // Beim Bearbeiten schliesst der Dialog wie bisher; beim Anlegen bleibt er
    // offen und zeigt das Angebot.
    if (justCreated.value === null) {
      modal?.hide();
    }
    await reload();
  } catch (e) {
    if (e instanceof ApiError && e.code === 'BAD_REQUEST' && e.fields) {
      invalidFields.value = new Set(e.fields);
      submitError.value   = 'Bitte Eingaben prüfen.';
    } else {
      submitError.value = errorMessage(e);
    }
  } finally {
    submitting.value = false;
  }
}

async function onDelete(sp: Servicepartner): Promise<void> {
  if (!confirm(`Servicepartner „${sp.description}" wirklich entfernen?`)) {
    return;
  }
  busyId.value = sp.id;
  try {
    await disable(sp.id);
    items.value = items.value.filter(x => x.id !== sp.id);
  } catch (e) {
    alert(`Löschen fehlgeschlagen: ${errorMessage(e)}`);
  } finally {
    busyId.value = null;
  }
}

function openAccounts(sp: Servicepartner): void {
  void accountsModal.value?.open(sp.id, sp.public_name ?? sp.description);
}

/**
 * Vom Angebot nach dem Anlegen ins Zugaenge-Modal. Der Sprung wartet auf das
 * hidden-Event des Stammdaten-Dialogs — sonst trifft das zweite Modal auf den
 * noch offenen Backdrop des ersten und die Seite bleibt gesperrt.
 */
function onCreateAccountForNew(): void {
  const created = justCreated.value;
  if (created === null) return;

  const el = modalEl.value;
  const openAccountsModal = () => {
    el?.removeEventListener('hidden.bs.modal', openAccountsModal);
    justCreated.value = null;
    void accountsModal.value?.open(created.id, created.public_name ?? created.description, true);
  };
  el?.addEventListener('hidden.bs.modal', openAccountsModal);
  modal?.hide();
}

function onCreateLater(): void {
  justCreated.value = null;
  modal?.hide();
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error)    return e.message;
  return 'Unbekannter Fehler.';
}
</script>

<style scoped>
.dashboard {
  max-width: 1600px;
}

.sp-actions-cell {
  width: 220px;
  white-space: nowrap;
}

/* Kleine Bildschirme (< md): Tabelle als gestapelte Karten statt Querscroll */
@media (max-width: 767.98px) {
  .dashboard-header-row {
    flex-wrap: wrap;
    gap: .75rem;
  }
  .dashboard-header-row .btn {
    width: 100%;
  }

  /* Kein Querscroll-Container mehr nötig */
  .table-responsive {
    overflow-x: visible;
  }

  .servicepartner-table thead {
    display: none;
  }

  .servicepartner-table,
  .servicepartner-table tbody,
  .servicepartner-table tr,
  .servicepartner-table td {
    display: block;
    width: 100%;
  }

  .servicepartner-table tr {
    margin-bottom: 1rem;
    padding: .5rem .9rem;
    border: 1px solid var(--bs-border-color, #dee2e6);
    border-radius: .5rem;
    background: var(--bs-body-bg, #fff);
  }

  /* Zebra-Streifen deaktivieren – im Kartenlayout stören sie */
  .servicepartner-table td {
    background-color: transparent !important;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: .35rem 0;
    border: none;
    border-bottom: 1px solid var(--bs-border-color-translucent, rgba(0, 0, 0, .075));
    text-align: right;
    word-break: break-word;
  }

  .servicepartner-table td:last-child {
    border-bottom: none;
  }

  .servicepartner-table td::before {
    content: attr(data-label);
    flex: 0 0 40%;
    font-weight: 600;
    text-align: left;
    color: var(--bs-secondary-color, #6c757d);
  }

  /* Aktionen: Buttons volle Breite, kein Label davor */
  .servicepartner-table td.sp-actions-cell {
    width: 100%;
    white-space: normal;
    justify-content: stretch;
    gap: .5rem;
    padding-top: .6rem;
  }
  .servicepartner-table td.sp-actions-cell::before {
    content: none;
  }
  .servicepartner-table td.sp-actions-cell .btn {
    flex: 1 1 0;
    margin: 0 !important;
  }
}
</style>
